/**
 * @file BindDomainDialog.tsx
 * @description «Привязать к магазину…» из журнала неизвестных адресов
 * (phase19.28 этап А; uiux-platform-shops §5). Выбор магазина — поиском
 * (`Combobox`, нативный select запрещён); адрес добавляется переадресацией
 * на главный, главным его делают позже, когда он заработает (В11).
 *
 * После привязки сразу запускаем проверку: хост в NPM мог уже отвечать
 * («Домен привязан — проверяем…»).
 */

import { useState } from "react";

import type { ShopListItem, UnknownDomain } from "../../../lib/api-platform-shops";
import { platformShops } from "../../../lib/api-platform-shops";
import { shopDomainError } from "../../../lib/platform/shop-errors";
import { Alert } from "../../ui/alert";
import { Button } from "../../ui/button";
import { Combobox } from "../../ui/combobox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Label } from "../../ui/label";
import { useNotify } from "../../ui/NotifyProvider";

interface Props {
  item: UnknownDomain | null;
  shops: ShopListItem[];
  onClose: () => void;
  onBound: () => void;
}

export function BindDomainDialog({ item, shops, onClose, onBound }: Props) {
  const notify = useNotify();
  const [shopId, setShopId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function close() {
    setShopId(null);
    setError(null);
    onClose();
  }

  async function submit() {
    if (!item) return;
    if (!shopId) {
      setError("Выберите магазин");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const domain = await platformShops.addDomain(shopId, { host: item.host });
      notify.success("Домен привязан — проверяем адрес…");
      // Проверка — вдогонку: её отказ (лимит раз в минуту) привязку не отменяет.
      void platformShops.recheck(shopId, domain.id).catch(() => undefined);
      setShopId(null);
      onBound();
    } catch (err) {
      setError(shopDomainError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={item !== null} onOpenChange={(open) => !open && !busy && close()}>
      <DialogContent fullScreen className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Привязать {item?.hostUnicode || item?.host}</DialogTitle>
          <DialogDescription>
            Адрес станет переадресацией на главный адрес магазина. Главным его можно сделать, когда он заработает.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="bind-domain-shop">Магазин</Label>
          <Combobox
            id="bind-domain-shop"
            options={shops.map((shop) => ({ value: shop.id, label: shop.displayName, description: shop.slug }))}
            value={shopId}
            onChange={setShopId}
            placeholder="Выберите магазин"
            searchPlaceholder="Название или slug"
            emptyText="Такого магазина нет"
            invalid={!!error && !shopId}
          />
        </div>
        {error ? <Alert severity="error">{error}</Alert> : null}
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={busy}>
            Отмена
          </Button>
          <Button onClick={() => void submit()} loading={busy}>
            Привязать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
